// src/v3/Tabs/index.tsx
import { cva as cva3 } from "class-variance-authority";
import {
  useMemo as useMemo2,
  useRef as useRef2,
  useState,
  useId
} from "react";

// src/v3/Tabs/Tab.tsx
import { cva } from "class-variance-authority";
import { forwardRef } from "react";
import { jsxs } from "react/jsx-runtime";
var tabVariants = cva(
  [
    "ui-appearance-none",
    "ui-block",
    "ui-duration-300",
    "ui-flex",
    "ui-items-center",
    "ui-justify-center",
    "ui-transition-colors"
  ],
  {
    variants: {
      inverted: {
        true: [],
        false: []
      },
      scrollable: {
        true: ["ui-whitespace-nowrap", "ui-snap-start"],
        false: []
      },
      selected: {
        true: [],
        false: []
      },
      style: {
        default: [
          "ui-bg-transparent",
          "ui-border-0",
          "ui-border-b-[3px]",
          "ui-border-solid",
          "ui-pb-4",
          "ui-pt-3",
          "ui-px-0",
          "ui-relative",
          "ui-text-sm",
          "ui-transition-opacity",
          "hover:ui-opacity-100",
          "lg:ui-pb-7",
          "lg:ui-pt-5",
          "lg:ui-px-12",
          "lg:ui-text-base"
        ],
        pill: [
          "ui-flex-1",
          "ui-px-5",
          "ui-py-3",
          "ui-rounded-[70px]",
          "ui-text-base"
        ]
      }
    },
    compoundVariants: [
      {
        className: "ui-border-b-current ui-opacity-100",
        style: "default",
        selected: true
      },
      {
        className: "ui-border-b-transparent ui-opacity-70",
        style: "default",
        selected: false
      },
      {
        className: "ui-bg-transparent",
        selected: false,
        style: "pill"
      },
      {
        className: "ui-text-sandstone-500",
        inverted: false,
        style: "pill"
      },
      {
        className: "ui-text-[#F0EBE4]",
        inverted: true,
        selected: false,
        style: "pill"
      },
      {
        className: "ui-bg-sandstone-300 ui-text-sandstone-500",
        selected: true,
        style: "pill"
      },
      {
        className: "ui-bg-white ui-text-sandstone-500",
        inverted: false,
        selected: true,
        style: "pill"
      }
    ]
  }
);
var Tab = forwardRef(function Tab2({
  className,
  icon,
  inverted = false,
  label,
  scrollable,
  selected,
  style,
  tabPanelId,
  ...props
}, ref) {
  return /* @__PURE__ */ jsxs("button", {
    ...props,
    "aria-controls": tabPanelId,
    "aria-selected": Boolean(selected),
    className: tabVariants({
      className,
      inverted,
      scrollable,
      selected,
      style
    }),
    "data-classname": className,
    "data-cy": "tabs_tab",
    ref,
    role: "tab",
    tabIndex: selected ? 0 : -1,
    type: "button",
    children: [
      icon,
      label
    ]
  });
});
Tab.displayName = "Tab";

// src/v3/Tabs/TabList.tsx
import { cva as cva2, cx } from "class-variance-authority";
import { jsx, jsxs as jsxs2 } from "react/jsx-runtime";
var tabListVariants = cva2(["ui-flex"], {
  variants: {
    inside: {
      true: ["ui-relative", "ui-z-10"],
      false: []
    },
    inverted: {
      true: [],
      false: []
    },
    orientation: {
      default: [],
      reverse: []
    },
    scrollable: {
      true: [],
      false: []
    },
    style: {
      default: ["ui-gap-x-8", "ui-relative", "ui-z-10"],
      pill: [
        "ui-bg-black",
        "ui-bg-opacity-10",
        "ui-gap-x-2",
        "ui-p-1",
        "ui-rounded-[100px]"
      ]
    }
  },
  compoundVariants: [
    {
      className: "ui-bg-opacity-25",
      inverted: true,
      style: "pill"
    },
    {
      className: "ui-bottom-8",
      inside: true,
      orientation: "reverse"
    },
    {
      className: "ui-top-8",
      inside: true,
      orientation: "default"
    },
    {
      className: "no-scrollbar ui-overflow-auto ui-justify-start ui-snap-x ui-snap-mandatory ui-snap-always",
      scrollable: true,
      style: "default"
    },
    {
      className: "ui-justify-center",
      scrollable: false,
      style: "default"
    }
  ]
});
function TabList({
  className,
  children,
  inside = false,
  inverted = false,
  orientation = "default",
  scrollable = false,
  style,
  tabWrapperComponent: Component = "div",
  containerClass = "",
  ...props
}) {
  return /* @__PURE__ */ jsx(Component, {
    children: /* @__PURE__ */ jsxs2("div", {
      className: cx("ui-relative", containerClass),
      children: [
        /* @__PURE__ */ jsx("div", {
          ...props,
          className: tabListVariants({
            className,
            inside,
            inverted,
            orientation,
            scrollable,
            style
          }),
          "data-cy": "tabs_tablist",
          role: "tablist",
          children
        }),
        style === "default" && /* @__PURE__ */ jsx("div", {
          className: "ui-absolute ui-bottom-0 ui-bg-[#CBCBCB] ui-h-[1px] ui-left-0 ui-w-full",
          role: "presentation"
        })
      ]
    })
  });
}
TabList.displayName = "TabList";

// src/v3/Tabs/TabPanel.tsx
import { useMemo, useRef } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
function TabPanel({
  content,
  id,
  selected,
  tabId,
  ...props
}) {
  const tabPanelRef = useRef(null);
  const tabIndex = useMemo(() => {
    var _a;
    const focusableChildren = (_a = tabPanelRef.current) == null ? void 0 : _a.querySelectorAll(
      'a[href], button, input, textarea, select, details, iframe, [tabindex]:not([tabindex="-1"])'
    );
    if (selected) {
      if (!(focusableChildren == null ? void 0 : focusableChildren.length)) {
        return 0;
      }
    } else {
      return -1;
    }
  }, [selected, tabPanelRef]);
  return /* @__PURE__ */ jsx2("div", {
    ...props,
    "aria-labelledby": tabId,
    "data-cy": "tabs_tabpanel",
    hidden: !selected,
    id,
    ref: tabPanelRef,
    role: "tabpanel",
    tabIndex,
    children: content
  });
}

// src/v3/Tabs/index.tsx
import { Fragment, jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
import { createElement } from "react";
var tabsVariants = cva3(["ui-flex", "ui-gap-y-10"], {
  variants: {
    orientation: {
      default: ["ui-flex-col"],
      reverse: ["ui-flex-col-reverse"]
    },
    style: {
      default: ["md:ui-gap-y-12"],
      pill: ["ui-flex-0", "ui-max-w-[415px]", "ui-w-full", "md:ui-gap-y-8"]
    }
  }
});
function Tabs({
  className,
  inside = false,
  inverted,
  orientation = "default",
  scrollableTablist,
  style = "default",
  tabList,
  tabs,
  tabPanels,
  onChange
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const tabsRef = useRef2(/* @__PURE__ */ new Map());
  const tabsId = useId();
  const tabIds = useMemo2(
    () => tabs.map((_, i) => `${tabsId}-tab-${i}`),
    [tabs, tabsId]
  );
  const tabPanelIds = useMemo2(
    () => tabPanels.map((_, i) => `${tabsId}-tabpanel-${i}`),
    [tabPanels, tabsId]
  );
  const handleTabListKeyDown = (event) => {
    const { key } = event;
    const focusOnNewTab = (newIndex) => {
      var _a;
      const newTabId = tabIds[newIndex];
      (_a = tabsRef.current.get(newTabId)) == null ? void 0 : _a.focus();
    };
    if (key === "ArrowRight") {
      setSelectedIndex((current) => {
        const newIndex = current === tabs.length - 1 ? 0 : current + 1;
        focusOnNewTab(newIndex);
        return newIndex;
      });
      onChange == null ? void 0 : onChange(selectedIndex === tabs.length - 1 ? 0 : selectedIndex + 1);
    }
    if (key === "ArrowLeft") {
      setSelectedIndex((current) => {
        const newIndex = current === 0 ? tabs.length - 1 : current - 1;
        focusOnNewTab(newIndex);
        return newIndex;
      });
      onChange == null ? void 0 : onChange(selectedIndex === 0 ? tabs.length - 1 : selectedIndex - 1);
    }
  };
  return /* @__PURE__ */ jsxs3("div", {
    className: tabsVariants({ className, orientation, style }),
    "data-cy": "tabs",
    "data-orientation": orientation,
    "data-style": style,
    children: [
      /* @__PURE__ */ jsx3(TabList, {
        ...tabList,
        inside,
        inverted,
        onKeyDown: handleTabListKeyDown,
        orientation,
        scrollable: scrollableTablist,
        style,
        children: /* @__PURE__ */ jsx3(Fragment, {
          children: tabs.map((tab, i) => /* @__PURE__ */ createElement(Tab, {
            ...tab,
            id: tabIds[i],
            inverted,
            key: tabIds[i],
            onClick: () => {
              setSelectedIndex(i);
              onChange == null ? void 0 : onChange(i);
            },
            ref: (node) => {
              const id = tabIds[i];
              if (node) {
                tabsRef.current.set(id, node);
              } else {
                tabsRef.current.delete(id);
              }
            },
            scrollable: scrollableTablist,
            selected: selectedIndex === i,
            style,
            tabPanelId: tabPanelIds[i]
          }))
        })
      }),
      tabPanels.map((tabPanel, i) => /* @__PURE__ */ createElement(TabPanel, {
        ...tabPanel,
        id: tabPanelIds[i],
        key: tabPanelIds[i],
        selected: selectedIndex === i,
        tabId: tabIds[i]
      }))
    ]
  });
}
export {
  Tabs
};
//# sourceMappingURL=index.mjs.map